/**
 * Preferência de refeições normalizada — propriedade única e vinculativa.
 *
 * Regras (genéricas, sem destinos especiais):
 *  - "restaurant"  → pausas com sugestão gastronómica real;
 *  - "free_break"  → pausa neutra, sem nome de estabelecimento nem Places;
 *  - "none"        → nenhuma refeição/pausa gastronómica; o tempo fica livre.
 *
 * A negação escrita ("sem restaurantes", "no restaurants", …) tem prioridade
 * sobre defaults, interesses e sugestões da IA. Duração 0 equivale a "none".
 */
import { detectMealIntent } from "./meal-intent";

export type MealPreference = "restaurant" | "free_break" | "none";

export type ResolveMealPreferenceInput = {
  /** Escolha explícita na interface (bloco "Refeições"). */
  choice?: MealPreference | null;
  /**
   * A escolha foi mesmo feita pelo utilizador nesta sessão (rádio tocado)?
   * Nesse caso ganha ao texto de pedidos anteriores — nunca há arrastamento
   * de uma preferência negativa de uma pesquisa antiga.
   */
  choiceExplicit?: boolean;
  /** Duração da refeição em minutos; 0 (ou negativo) desliga as refeições. */
  eatMinutes?: number | null;
  /** Texto do pedido do utilizador (multilingue). */
  requestText?: string | null;
};

export function resolveMealPreference(input: ResolveMealPreferenceInput): MealPreference {
  const minutes = Number(input.eatMinutes ?? NaN);
  const zeroMinutes = Number.isFinite(minutes) && minutes <= 0;
  // Intenção escrita no pedido ATUAL (null = o texto nada diz).
  const written = detectMealIntent(input.requestText ?? "");

  // Pedido escrito que menciona explicitamente restaurantes tem prioridade
  // absoluta sobre uma escolha manual "não incluir refeições". Isto evita
  // payloads contraditórios ("restaurante japonês" + mealPreference none).
  if (written === "restaurant" && (!input.choice || input.choice === "none")) {
    return "restaurant";
  }

  if (input.choice === "none") return "none";
  // Escolha manual feita depois de analisar o pedido atual tem prioridade.
  if (input.choiceExplicit && input.choice) return input.choice;
  // Duração 0 ⇒ refeições desativadas, independentemente de tudo o resto.
  if (zeroMinutes) return "none";
  // A intenção escrita nunca pode ser sobreposta por defaults/IA/interesses.
  if (written) return written;
  if (input.choice === "free_break") return "free_break";
  // Default NEUTRO RESTAURADO: almoço/jantar com sugestões de restaurante é
  // o comportamento natural de um itinerário. Isto NÃO ativa comida como
  // paragem de visita — esse é o sinal separado foodStopIntent
  // (src/lib/food-intent.ts, garantia vinculativa A).
  return "restaurant";
}


/**
 * Estado de refeições de um PEDIDO NOVO — regra única e sem herança.
 *
 * Precedência: escolha explícita atual > texto do pedido atual > default.
 * Um pedido novo nunca herda a negação do anterior: se o texto atual não
 * exclui refeições, a duração volta ao valor por defeito mesmo que o pedido
 * anterior a tenha posto a zero.
 */
export type MealRequestState = {
  preference: MealPreference;
  /** Valor a repor no rádio da interface. */
  choice: MealPreference;
  eatMinutes: number;
  /** Houve uma exclusão anterior que foi limpa por este pedido? */
  clearedPreviousExclusion: boolean;
};

export function nextMealStateForRequest(input: {
  text: string;
  /** Duração atualmente configurada (pode vir a zero de um pedido anterior). */
  eatMinutes: number;
  defaultEatMinutes: number;
  /** Escolha manual feita para ESTE texto (rádio tocado depois de o escrever). */
  explicitChoice?: MealPreference | null;
}): MealRequestState {
  const written = detectMealIntent(input.text ?? "");
  // Um pedido escrito que menciona explicitamente restaurantes vence a escolha
  // manual "não incluir refeições" — nunca se gera um payload contraditório.
  const explicit =
    written === "restaurant" && input.explicitChoice === "none" ? null : input.explicitChoice;
  // Sem escolha nem intenção escrita: modo de refeições normal (almoço/jantar
  // com sugestões) — comida como PARAGEM DE VISITA é outro sinal, em
  // src/lib/food-intent.ts, e continua desligado por defeito.
  const choice = explicit ?? written ?? "restaurant";
  const current = Number(input.eatMinutes);
  const zeroed = !Number.isFinite(current) || current <= 0;
  const eatMinutes =
    choice === "none" ? 0 : zeroed ? Math.max(1, input.defaultEatMinutes) : current;
  const preference = resolveMealPreference({
    choice,
    choiceExplicit: Boolean(explicit),
    eatMinutes,
    requestText: input.text,
  });
  return {
    preference,
    choice,
    eatMinutes,
    clearedPreviousExclusion: zeroed && preference !== "none",
  };
}


/** Há bloco de refeição no horário? */
export const mealsEnabled = (preference: MealPreference) => preference !== "none";

/**
 * Sinal SEPARADO de almoço/jantar (cartões de refeição). Vive de propósito
 * ao lado de `MealPreference`: `mealMode` controla apenas os cartões de
 * almoço/jantar e NUNCA ativa restaurantes como paragens de visita — isso é
 * `foodStopIntent` em src/lib/food-intent.ts.
 */
export type MealMode = "restaurant_suggestions" | "free_break" | "none";

/** Deriva o mealMode do sinal de refeições resolvido do pedido. */
export const mealModeFor = (preference: MealPreference): MealMode =>
  preference === "restaurant" ? "restaurant_suggestions" : preference;

/**
 * Remove frases de exclusão gastronómica de um texto livre (notas guardadas),
 * para que uma pesquisa antiga nunca contamine a seguinte. Multilingue e
 * genérico: só apaga a frase da negação, mantém o resto do pedido.
 */
export function stripFoodExclusions(text: string): string {
  if (!text) return text;
  const NEGATION =
    /[^.;!?\n]*\b(?:sem|não\s+(?:preciso|quero|incluir)[^.;!?\n]*?|no|without|sin|sans|keine|niente|senza)\b[^.;!?\n]*\b(?:restaurante?s?|refei(?:ção|ções)|almoç[oa]s?|jantar(?:es)?|gastronomia|comida|food|meals?|lunch|dinner|comida|essen)\b[^.;!?\n]*[.;!?]?/gi;
  return text.replace(NEGATION, " ").replace(/\s{2,}/g, " ").trim();
}

/**
 * Foco exclusivo de categoria manda sobre a preferência derivada do texto.
 * Um itinerário de doçaria nunca tem refeições — nem depois de otimização,
 * enriquecimento ou render. Sem foco (ou foco diferente), nada muda.
 */
export function mealPreferenceForFocus(
  categoryFocus: string | null | undefined,
  base: MealPreference,
): MealPreference {
  return categoryFocus === "sweets" || categoryFocus === "breakfast" ? "none" : base;
}
